/* eslint-disable @typescript-eslint/no-empty-function */
import type { ProductVideo as ProductVideoSchema } from '../../../amplienceTypes/schemas/imported/product-detail-info-schema';
import type { StyleVariant } from '../../graphql/codegen';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { ReactNode } from 'react';

import { createContext, useContext, useState, useMemo, useEffect } from 'react';

import { useMediaQueryContext } from './MediaQueryContext';
import { styleVariantsLimitForSSR } from '../../constants';
import { useStyleVariantsQuery } from '../../graphql/codegen';
import { useAppContext } from '../static/AppContext';
import { usePDPContext } from '../static/PDPContext';
import { useStaticContext } from '../static/StaticContext';
import { findProfileImage } from '~/utilities/helpers/findImageProfile';

export interface ProductVideo {
  content: ProductVideoSchema;
  [k: string]: unknown;
}

type GalleryContextType = {
  galleryZoomIn: boolean;
  setGalleryZoomIn: (galleryZoomIn: boolean) => void;
  scrolledGalleryMode: boolean;
  styleVariantsHoveredIndex: number;
  productStyleVariantIndex: number;
  hoverColorCarouselIndex: (styleVariantsSelectedIndex: number) => void;
  styleVariants: Maybe<StyleVariant>[] | null | undefined;
};

const GalleryContext = createContext<GalleryContextType>({
  galleryZoomIn: false,
  setGalleryZoomIn: () => {},
  scrolledGalleryMode: false,
  styleVariantsHoveredIndex: -1,
  productStyleVariantIndex: -1,
  hoverColorCarouselIndex: () => {},
  styleVariants: null,
});

export const useGalleryContext = () => useContext(GalleryContext);

export const GalleryContextProvider = ({ children }: { children: ReactNode }) => {
  const { isDesktop } = useMediaQueryContext();
  const [galleryZoomIn, setGalleryZoomIn] = useState<boolean>(false);
  const [scrolledGalleryMode, setScrolledGalleryMode] = useState<boolean>(isDesktop);
  const { product } = usePDPContext();
  const { availableStyleVariantsSorted, code, baseProduct } = product;
  const { locale, siteType } = useAppContext();
  const {
    configuration: { allowedGlobalImageTypes },
  } = useStaticContext();

  const currentCode = useMemo(
    () => (baseProduct || code?.split('-').slice(0, 3).join('-'))?.toUpperCase(),
    [baseProduct, code]
  );

  const { loading, error, data } = useStyleVariantsQuery({
    variables: { siteType, locale, code },
    skip: availableStyleVariantsSorted?.length !== styleVariantsLimitForSSR,
    ssr: false,
  });

  const { styleVariants, productStyleVariantIndex } = useMemo(() => {
    const variants =
      !error && !loading && data
        ? data.product.availableStyleVariantsSorted
        : availableStyleVariantsSorted;

    if (!variants?.length || !currentCode) {
      return { styleVariants: variants, productStyleVariantIndex: -1 };
    }

    const index = variants.findIndex(v => v?.code?.toUpperCase() === currentCode);

    if (index !== -1) {
      return { styleVariants: variants, productStyleVariantIndex: index };
    }

    const { name, url, color, mainColor, colorData, simplifiedImages } = product;
    const imageMap = new Map(simplifiedImages?.entry?.map(e => [e?.key, e?.value]));
    const thumb =
      findProfileImage(imageMap)(allowedGlobalImageTypes) ?? simplifiedImages?.entry?.[0]?.value;

    const newVariant: StyleVariant = {
      code: currentCode,
      name,
      productUrl: url,
      color: color?.description,
      mainColor: mainColor?.description,
      colorData,
      images: {
        thumbnail: thumb?.url,
        thumbnailAltText: thumb?.altText ?? name,
      },
    };

    const updatedStyleVariants = [...variants, newVariant].sort((a, b) =>
      (a?.code ?? '').localeCompare(b?.code ?? '')
    );

    return {
      styleVariants: updatedStyleVariants,
      productStyleVariantIndex: updatedStyleVariants.findIndex(
        v => v?.code?.toUpperCase() === currentCode
      ),
    };
  }, [
    allowedGlobalImageTypes,
    availableStyleVariantsSorted,
    currentCode,
    data,
    error,
    loading,
    product,
  ]);

  const [styleVariantsHoveredIndex, setStyleVariantsHoveredIndex] =
    useState(productStyleVariantIndex);

  useEffect(() => {
    setStyleVariantsHoveredIndex(productStyleVariantIndex);
  }, [productStyleVariantIndex]);

  useEffect(() => {
    setScrolledGalleryMode(isDesktop);
  }, [setScrolledGalleryMode, isDesktop]);

  const value = useMemo<GalleryContextType>(
    () => ({
      galleryZoomIn,
      setGalleryZoomIn,
      scrolledGalleryMode,
      styleVariantsHoveredIndex,
      productStyleVariantIndex,
      hoverColorCarouselIndex: setStyleVariantsHoveredIndex,
      styleVariants,
    }),
    [
      galleryZoomIn,
      scrolledGalleryMode,
      styleVariants,
      styleVariantsHoveredIndex,
      productStyleVariantIndex,
    ]
  );

  return <GalleryContext value={value}>{children}</GalleryContext>;
};
